/**
 * VoiceAttribution
 *
 * Always pair Voice identity (VoiceBadge) with authorship axes
 * (source × review) so non-human seats are unmistakable vs humans.
 *
 * Optional "Mark as reviewed" — never required; only when the caller
 * provides onMarkReviewed.
 */

import { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { VoiceBadge } from './VoiceBadge';
import { CreationProvenanceBadge } from '../../../components/prioritization/provenance/CreationProvenanceBadge';
import type {
  CreationSource,
  CreationReviewStatus,
  CreationProvenance,
} from '../../../types/prioritization/creationProvenance';
import { resolveAuthorship } from '../../../types/prioritization/creationProvenance';
import { MARK_REVIEWED_LABEL, MARKING_REVIEWED_LABEL } from '../../../lib/items/reviewVocabulary';

export function VoiceAttribution({
  badgeLabel = 'Voice',
  source,
  reviewStatus,
  provenance,
  provenanceVariant = 'label',
  compact = false,
  className = '',
  canMarkReviewed = false,
  onMarkReviewed,
}: {
  badgeLabel?: string;
  source?: CreationSource;
  reviewStatus?: CreationReviewStatus;
  /** @deprecated Prefer source + reviewStatus. */
  provenance?: CreationProvenance;
  provenanceVariant?: 'badge' | 'label';
  compact?: boolean;
  className?: string;
  canMarkReviewed?: boolean;
  onMarkReviewed?: () => Promise<void> | void;
}) {
  const [busy, setBusy] = useState(false);
  const authorship = resolveAuthorship({ source, reviewStatus, provenance });
  const showMark =
    canMarkReviewed &&
    onMarkReviewed &&
    authorship.source !== 'human' &&
    authorship.reviewStatus === 'none';

  async function handleMark() {
    if (!onMarkReviewed || busy) return;
    setBusy(true);
    try {
      await onMarkReviewed();
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className={`inline-flex items-center gap-1.5 flex-wrap ${className}`}>
      <VoiceBadge label={badgeLabel} size="sm" />
      <CreationProvenanceBadge
        source={authorship.source}
        reviewStatus={authorship.reviewStatus}
        variant={provenanceVariant}
        compact={compact}
      />
      {showMark && (
        <button
          type="button"
          onClick={() => void handleMark()}
          disabled={busy}
          title="Optionally mark this Voice content as human-reviewed. Review is never required."
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-[#3A7D56] text-white hover:bg-[#2f6647] disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
          {busy ? MARKING_REVIEWED_LABEL : MARK_REVIEWED_LABEL}
        </button>
      )}
    </span>
  );
}
